import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { NavigationEnd, provideRouter, Router } from '@angular/router';
import { filter } from 'rxjs';

import { routes } from './app.routes';

/**
 * Scroll al tope al cambiar de pantalla, y sólo ahí.
 *
 * Antes esto era `withInMemoryScrolling({ scrollPositionRestoration: 'top' })`,
 * que sube el scroll en **toda** navegación. El problema es que el buscador
 * navega contra sí mismo: cada zoom, arrastre o clic en un pin del mapa
 * termina en `sincronizarUrl()`, que reescribe `cam` y `pin` con
 * `router.navigate(..., { replaceUrl: true })`. Para el router eso es una
 * navegación como cualquier otra, así que el usuario perdía la posición y
 * volvía arriba de todo apenas tocaba el mapa.
 *
 * Acá el scroll mira sólo el path: `/buscador?cam=…` → `/buscador?cam=…` no
 * mueve nada, y `/buscador` → `/propiedades/:id` sí. De paso los filtros y el
 * alternador grilla/mapa dejan de saltar, que era el mismo síntoma.
 */
function provideScrollAlCambiarDePantalla() {
  return provideAppInitializer(() => {
    const router = inject(Router);
    const scroller = inject(ViewportScroller);
    let pathAnterior: string | null = null;

    router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        const path = e.urlAfterRedirects.split('?')[0];
        if (path === pathAnterior) return;
        pathAnterior = path;
        scroller.scrollToPosition([0, 0]);
      });
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideScrollAlCambiarDePantalla(),
    provideHttpClient(),
    // FP-31: la ficha llega renderizada del servidor. La hidratación reusa ese
    // DOM y, por la caché de transferencia, el detalle que ya pidió el
    // servidor. El resto de las rutas se renderizan en el navegador.
    provideClientHydration(withEventReplay()),
  ],
};
